import React, { useState } from 'react';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import Switch from '@components/catalog/switch/switch';
import { Alert } from '@interfaces/models/alert';
import { useContextSelector } from 'use-context-selector';
import { AlertsContext } from '@context/alerts.context';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import clsx from 'clsx';
import AlertDeletionModal from '@components/alerts/alert-deletion-modal/alert-deletion-modal';
import { ModalContext } from '@context/modal.context';
import Icon from '@components/atoms/icon/icon';
import AlertPausedFlag from '../alert-paused-flag/alert-paused-flag';

import styles from './alert-manage-snippet.module.scss';

interface AlertManageSnippetProps {
  alert: Alert;
  onError: (error: string) => void;
  alertIndex: number;
}

const AlertManageSnippet: React.FC<AlertManageSnippetProps> = ({ alert, onError, alertIndex }): React.JSX.Element => {
  const { t } = useTranslation();
  const { t: tA11y } = useTranslation('a11y');
  const openModal = useContextSelector(ModalContext, (v) => v.openModal);
  const closeModal = useContextSelector(ModalContext, (v) => v.closeModal);
  const deleteNewInsAlert = useContextSelector(AlertsContext, (v) => v.deleteNewInsAlert);
  const updateAlertNotificationState = useContextSelector(AlertsContext, (v) => v.updateAlertNotificationState);
  const [notificationState, setNotificationState] = useState<boolean>(!alert.isPaused && alert.notificationState);
  const { sendAnalyticEvent: sendAlertAnalyticEvent } = useAnalyticEvents('alerts');

  // TODO: Remove when the testing is done
  const { query } = useRouter();
  const enableErrors = query?.enableErrors === 'true' ? true : false;

  const toggleNotifications = async (val: boolean) => {
    try {
      onError('');

      // TODO: Remove when the testing is done
      await updateAlertNotificationState(enableErrors ? `${alert.id}testing` : alert.id, {
        enabled: val,
      });

      // await updateAlertNotificationState(alert.id, { enabled: val });
      setNotificationState(val);
      sendAlertAnalyticEvent(val ? 'alert_activate_notifications' : 'alert_deactivate_notifications');
    } catch (error) {
      onError(error);
    }
  };

  const handleAlertDeletion = async () => {
    try {
      // TODO: Remove when the testing is done
      await deleteNewInsAlert(enableErrors ? `${alert.id}testing` : alert.id);

      // await deleteNewInsAlert(alert.id);
      sendAlertAnalyticEvent('alert_delete');
      closeModal?.();
    } catch (error) {
      onError(error);
      closeModal?.();
    }
  };

  const handleAlertDeleteClickHandler = () =>
    openModal(
      (modalDialogTitleId) => (
        <AlertDeletionModal
          onClose={closeModal}
          deleteConfirm={handleAlertDeletion}
          modalDialogTitleId={modalDialogTitleId}
        />
      ),
      {
        styles: {
          content: {
            maxWidth: '600px',
          },
        },
      },
    );

  const handleDeleteClick = () => {
    onError('');
    handleAlertDeleteClickHandler();
  };

  return (
    <>
      <div className={styles.alertSnippet__header}>
        <div
          className={clsx(styles.alertSnippet__title, 'vc-text-xl')}
          data-cy="alert-management-snippet-title"
        >
          {alert?.title}
        </div>
        <button
          className={clsx(styles.alertSnippet__deleteBtn, 'vc-text-m')}
          type="button"
          id={`alertSnippet_id_${alertIndex}`}
          aria-haspopup="dialog"
          data-cy="manageAlerts_delete"
          data-vc-dd-action-name="manageAlerts_delete"
          onClick={handleDeleteClick}
        >
          {t('ALERTS.MANAGEMENT.DELETE')}
        </button>
      </div>
      {alert?.subtitle && (
        <p
          className={clsx(styles.alertSnippet__filters, 'vc-text-m')}
          data-cy="alert-management-snippet-filters"
        >
          {`${t('ALERTS.LANDING.FILTERS_TEXT')} ${alert?.subtitle}`}
        </p>
      )}
      <div className={styles.alertSnippet__notif}>
        <div className={styles.alertSnippet__notif__left}>
          <div
            id={`alertSnippet__labelSwitch${alert.id}`}
            className={clsx(styles.alertSnippet__notif__text, 'vc-text-l')}
            data-cy="alert-management-snippet-notification-state"
          >
            <Icon
              name={notificationState ? 'alert-outline-default' : 'notification-off-default'}
              ariaHidden
            />
            {t(notificationState ? 'ALERTS.MANAGEMENT.NOTIFICATIONS_ON' : 'ALERTS.MANAGEMENT.NOTIFICATIONS_OFF')}
          </div>
        </div>
        <span
          className="visuallyhidden"
          id={`alertSnippet__labelSwitch${alert.id}__span`}
        >
          {alert?.notificationState ? tA11y('A11Y.SAVED_SEARCHES.TOGGLE_ON') : tA11y('A11Y.SAVED_SEARCHES.TOGGLE_OFF')}
        </span>
        <span className={styles.alertSnippet__notif__switch}>
          <Switch
            checked={notificationState}
            data-vc-dd-action-name="manageAlerts_updateNotification"
            ariaLabelledby={`alertSnippet__labelSwitch${alert.id}__span`}
            onChange={(val) => toggleNotifications(val)}
            dataCyElementLocator={
              alert?.notificationState
                ? 'alert-management-snippet-notification-on'
                : 'alert-management-snippet-notification-off'
            }
          />
        </span>
      </div>
      {alert?.isPaused && !notificationState && (
        <div className={styles.alertSnippet__pausedFlag}>
          <AlertPausedFlag />
        </div>
      )}
    </>
  );
};

export default AlertManageSnippet;
